import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next"
import { colors, fonts } from "../../../Styles"
import { AttentionCard, NewModal, NewSelect, MainContentPaddingTypes, Button } from "../..";
import { AttentionTypes, NewFieldStyles, SelectFieldOption_Values } from "../../../Definitions";
import { useResize } from "../../_Components/";
import { useAppSelector } from "../../../Store";
import { Graph_QL, useGetDomainLog } from "../../../Services";
import { useLanControllers } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useLanControllers";
import { LanControllerLogArgs } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLanControllerLogs";
import { useTerminals } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useTerminals";
import { useSection } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";

const SYSTEM_ID = 1; // TODO: Get this from the system

// Log View
type SimpleLogViewProps = {
  data: Record<string, any>;
  style?: React.CSSProperties;
};

const SimpleLogView = ({ data, style }: SimpleLogViewProps) => {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "10px",
        width: "100%",
        ...style,
      }}
    >
      {Object.entries(data).map(([key, value]) => (
        <Fragment key={key}>
          <div>{key}:</div>
          <div>{String(value)}</div>
        </Fragment>
      ))}
    </div>
  );
};

// Filter Options
const filterOptions = [
  {label: "Date", id: 0},
  {label: "Level", id: 1},
  {label: "Type", id: 2},
] as SelectFieldOption_Values[];

/**
 * Attentions
 * @returns A widget with AttentionCards based on pens with alert or warning in the system. Each card opens a Modal with more Pen informations.
 */
export default function Attentions() {
  const {t} = useTranslation();
  const [activeLogId, setActiveLogId] = useState<number | null>(null); // Pen Id from Attention to show Pen Modal
  const [selectedFilterOption, setSelectedFilterOption] = useState<SelectFieldOption_Values | undefined>(); // Filter option for sorting the logs
  const contentResizeRef = useRef<HTMLDivElement>(null)
  const { width } = useResize(contentResizeRef)
  
  const smallContent = useMemo(() => { 
    if (width > 0 && width < 350) return true;
    return false;
  }, [width])

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({ domain: farm })), [farm]);
  
  const { data: logData } = useGetDomainLog() //todo: current LogData is redux and containing both Domain log and LanController log
  const { call: getLanControllers, data: lanControllerData, getLanControllerLogs } = useLanControllers(connection)
  const { call: getTerminals, data: terminals } = useTerminals(connection)  
  const { call: getLfSectionsInfo, data: lfSectionsState } = useSection(connection)

  useEffect(() => {
    getLanControllers({ fetchAmount: 50 });
    getTerminals({ fetchAmount: 50 })
    getLfSectionsInfo({ ids: [SYSTEM_ID] }, connection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useMemo(() => {
    if (!lanControllerData[domain]) {
      return <div>{t("error.noData", { defaultValue: "No data available" })}</div>;
    }

    const lanControllers = lanControllerData[domain].data?.filter(lanController => lanController.system_id === SYSTEM_ID) || [];

    const today = Math.round(Date.now()/1000)
    const weekAgo = 0;
    return lanControllers.map((lanController, index) => {
      if (lanController.lan_controller_id) {
        getLanControllerLogs({ starttime: weekAgo, endtime: today, lancontrollerid: lanController.lan_controller_id})
      }
      return (
        <div key={index}>
          {lanController.name}
          {lanController.controller_id}
          {lanController.system_id}
        </div>
      )
    })
  }, [domain, getLanControllerLogs, lanControllerData, t])

  const logs = useMemo(() => {
    let data: LanControllerLogArgs[] = [];
  
    if (logData && Object.entries(logData).length) {
      const controllerData = Object.entries(logData)[0][1].data as unknown as LanControllerLogArgs[];
  
      data = controllerData
        .filter(log => {
          return (log.level === 0 || log.level === 1) && (log.type === 1 || log.type === 2);
        });

      if (selectedFilterOption?.id === 1) {
        data.sort((a, b) => {
          if (a.level !== b.level) return a.level - b.level;
          return b.timestamp - a.timestamp;
        });
      } else if (selectedFilterOption?.id === 2) {
        data.sort((a, b) => {
          if (a.type !== b.type) return a.type - b.type;
          return b.timestamp - a.timestamp;
        });
      } else {
        data.sort((a, b) => b.timestamp - a.timestamp);
      }
    }
  
    return data;
  }, [logData, selectedFilterOption]);

  const AttentionCards = useMemo(() => {
    return logs?.map((log, index) => {
      let TerminalData = terminals[domain]?.data.find((terminal) => terminal.terminal_id === log.fk_terminals);
      let locationResult: String | undefined = undefined;
      lfSectionsState[domain]?.data.forEach((section) => {
        section.section_locations.forEach((location) => {
          if (location.location_id === TerminalData?.fk_locations) {
            locationResult = location.name;
          }
        });
      })
      return (
        <AttentionCard
          hover
          key={index}
          onLeftClick={() => {
            setActiveLogId(log.lan_controller_log_id);
          }}
          onRightClick={() => console.log("delete")}
          attentionType={log.level === 0 ? AttentionTypes.alert : AttentionTypes.warning}
          systemContent={
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between" }}>
              <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{locationResult ? `Pen: ${locationResult}` : ""}</p>
            </div>
          }
          problemContent={ <>
            <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{log.message}</p>
            <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{new Date(log.timestamp * 1000).toLocaleString()}</p>
          </> }
        />
        )
      ;
    })}, [logs, domain, lfSectionsState, terminals]);

  const currentIndex = useMemo(() => {
    return logs.findIndex(log => log.lan_controller_log_id === activeLogId);
  }, [logs, activeLogId]);
  
  const LogInfo = useMemo(() => { 
    const simpleLog =logs?.find(log => log.lan_controller_log_id === activeLogId);
  
    if (simpleLog) {
      const displayData = {
        Fk_lan_controllers: simpleLog.fk_lan_controllers,
        Lan_controller_log_id: simpleLog.lan_controller_log_id,
        Level: simpleLog.level,
        Type: simpleLog.type,
        Message: simpleLog.message,
        Time: new Date(simpleLog.timestamp * 1000).toLocaleString(),
      };
  
      return (
        <SimpleLogView
          data={displayData}
          style={{
            ...fonts.paragraph,
            ...fonts.fontSize4,
            color: colors.greyLightTertiary,
          }}
        />
      );
    }
  }, [logs, activeLogId]);

  const bannerContent = useMemo(() => {
    let problemsTotal = logs.filter(pen => pen.level === 0).length;
    const problemText = problemsTotal === 1 ? t("systemControls.general.problem", {defaultValue: "Problem"}) : t("systemControls.general.problems", {defaultValue: "Problems"});
    let warningsTotal = logs.filter(pen => pen.level === 1).length;
    const warningsText = warningsTotal === 1 ? t("systemControls.general.warning", { defaultValue: "Warning" }) : t("systemControls.general.warnings", { defaultValue: "Warnings" });
    return (
      <div style={{ gridArea: "2/1/3/2", display: "flex", alignItems: "center", justifyContent: smallContent ? "center" : "left", padding: smallContent ? "0px" : "0px 20px", backgroundColor: colors.greyTertiary, height: "30px", ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary, boxSizing: "border-box" }}>
        {problemsTotal} {problemText} | {warningsTotal} {warningsText}
      </div>
    )
  }, [logs, t, smallContent])
    
  return(
    <>
      <div style={{width: "100%", maxWidth: "410px", height: "100%", backgroundColor: colors.greyLightTertiary, borderRadius: "10px", border: "5px solid "+colors.greyPrimary, boxSizing: "border-box", display: "grid", gridTemplateColumns: "1fr", gridTemplateRows: "75px 30px 1fr"}}>
        <div style={{gridArea: "1/1/2/2", width: "100%", height: "100%", display: "flex", flexDirection: "column", padding: smallContent ? "0px" : "0px 20px", alignItems: smallContent ? "center" : "left", justifyContent: "center", gap: "10px", backgroundColor: colors.greyPrimary, boxSizing: "border-box"}}>
          <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary}}>{t("systemControls.general.attentions", {defaultValue: "Attentions"})}</p>
          <NewSelect 
            placeholder={t("filterBy", {defaultValue: "Filter By"})}
            options={filterOptions}
            onChange={(selectedOption) => setSelectedFilterOption(selectedOption)}
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greySecondary,
              fieldWidth: "100%"
            }}
            valueOverrides={{
              defaultValue: filterOptions[0]
            }}
          />
        </div>
        {bannerContent}
        <div style={{margin: "10px 0px", overflowY: "scroll", height: "calc(100% - 20px)", boxSizing: "border-box", scrollbarColor: colors.greyTertiary+colors.transparent, scrollbarWidth: "thin"}}>
          <div ref={contentResizeRef} style={{gridArea: "3/1/4/2", display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "center", justifyContent: smallContent? "center" : "start", gap: "10px", maxWidth: "350px", margin: "0 auto"}}>
            {AttentionCards}
          </div>
        </div>
      </div>
      {/* ------------- Pen Modal -------------- */}
      <NewModal
        modalState={!!activeLogId}
        header={"Attentions"}
        subHeader={`Log ID: ${activeLogId}`}
        overrides={{
          mainContentPadding: MainContentPaddingTypes.secondary
        }}
        predefinedButtons={{
          onClose: () => setActiveLogId(null),
          onNext: () => setActiveLogId(logs[(currentIndex + 1) % logs.length].lan_controller_log_id),
          onPrev: () => setActiveLogId(logs[(currentIndex - 1 + logs.length) % logs.length].lan_controller_log_id)
        }}
        secondaryContent={LogInfo ? LogInfo : <></>}
        footerContent={
          <Button
            content={"Delete"}
            onClick={() => {
              console.log("delete");
              setActiveLogId(logs[(currentIndex + 1) % logs.length].lan_controller_log_id);
            }}
          />
        }
      />
      {/* ----------- Pen Modal Stop ----------- */}
    </>
  )
}